import { useMemo } from 'react'

/**
 * Deep equality used for dirty checking. Walks plain objects and arrays -
 * not Maps, Sets, Dates or class instances. Forms in this app store flat
 * value objects (strings, numbers, bools, line arrays of the same), so the
 * shallow scope is enough.
 *
 * Why not JSON.stringify? Key order is implementation-defined; comparing
 * `{a:1,b:2}` against `{b:2,a:1}` would falsely flag the form as dirty.
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null || b == null) return false
  if (typeof a !== 'object' || typeof b !== 'object') return false
  if (Array.isArray(a) !== Array.isArray(b)) return false
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], b[i])) return false
    return true
  }
  const ar = a as Record<string, unknown>
  const br = b as Record<string, unknown>
  const aKeys = Object.keys(ar)
  const bKeys = Object.keys(br)
  if (aKeys.length !== bKeys.length) return false
  for (const k of aKeys) if (!deepEqual(ar[k], br[k])) return false
  return true
}

/**
 * Returns true when `current` differs from `baseline`. When baseline is
 * null/undefined the form is considered new - Save should always be enabled
 * unless the caller adds its own empty-form gate.
 *
 * Usage:
 *   const dirty = useDirty(form, savedRecord)
 *   <Button disabled={!dirty || mutation.isPending}>Save</Button>
 */
export function useDirty<T>(current: T, baseline: T | null | undefined): boolean {
  return useMemo(() => {
    if (baseline == null) return true
    return !deepEqual(current, baseline)
  }, [current, baseline])
}
